// src/contexts/LocaleProvider.tsx
'use client';

import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Locale, findLocale } from '../utils/Locale';

interface LocaleContextValue extends Locale {
    setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
    ...findLocale('en'),
    setLocale: () => {},
});

interface LocaleProviderProps {
    children: ReactNode;
}

export const LocaleProvider = ({ children }: LocaleProviderProps) => {
    // SSR 先給安全預設，避免觸碰瀏覽器 API
    const [locale, setLocale] = useState<Locale>(findLocale('en'));

    // 僅在瀏覽器讀 localStorage / navigator
    useEffect(() => {
        try {
            const stored = window.localStorage.getItem('locale') || undefined;
            const preferredList = navigator.languages?.length
                ? navigator.languages
                : [navigator.language || 'en'];

            const candidates = [stored, ...preferredList].filter((l): l is string => Boolean(l));

            setLocale(findLocale(candidates));
        } catch {
            // 任何例外維持 'en'
            setLocale(findLocale('en'));
        }
    }, []);

    const handleSetLocale = (next: string) => {
        try {
            window.localStorage.setItem('locale', next);
        } catch {}
        setLocale(findLocale(next));
    };

    const value = useMemo(() => ({ ...locale, setLocale: handleSetLocale }), [locale]);

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);
